import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { NavigationError, Router, RouterModule, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { ClientSelectorComponent } from './client-selector/client-selector.component';
import { environment } from '../environments/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { AccessToken } from './access-token';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, fromEvent, interval, merge } from 'rxjs';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialog } from './error-dialog/error-dialog';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSidenavModule,
    MatListModule,
    MatSnackBarModule,
    ClientSelectorComponent,
    RouterModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App implements OnInit {
  protected readonly title = signal('Halo Lite');

  router = inject(Router);
  location = inject(Location);
  private oauth = inject(OAuthService);

  errorEvent = toSignal(
    merge(this.router.events, this.oauth.events).pipe(
      filter((e) => e instanceof NavigationError || e instanceof OAuthErrorEvent),
    ),
  );

  documentEvent = toSignal(
    merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'click'),
    ),
  );
  clock = toSignal(interval(1 * 1000));

  isNavigating = computed(() => {
    const nav = this.router.currentNavigation();
    if (nav) {
      return nav?.extras?.state ? nav.extras.state['showLoading'] : true;
    }
    return false;
  });

  private last_action: number = Date.now();
  readonly dialog = inject(MatDialog);

  searchOpen = signal(false);
  searchQuery = signal('');

  constructor() {
    //Tap into the document events
    effect(() => {
      if (this.documentEvent()) this.last_action = Date.now();
    });

    //Tap into our clock to check for inactivity
    effect(() => {
      if (this.clock()) {
        const now = Date.now();
        const idleFor = now - this.last_action;
        if (idleFor > 15 * 60 * 1000) {
          console.log('User is idle for more than 15 minutes, logging out.');
          this.oauth.logOut();
        }
      }
    });

    effect(() => {
      if (this.errorEvent()) {
        this.dialog.open(ErrorDialog, {
          data: this.errorEvent(),
        });
      }
    });
  }

  ngOnInit() {
    this.clients = new AccessToken(this.oauth.getAccessToken()).clients;
    if (this.clients && this.clients.length > 0) {
      sessionStorage.setItem('activeClient', this.clients[0]);
    }
  }

  clients: string[] | null = null;
  policyUrl = environment.policy_url;
  termsUrl = environment.terms_url;

  toggleSearch() {
    this.searchOpen.update((open) => !open);
  }

  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.searchQuery.set(target?.value ?? '');
  }

  submitSearch() {
    const query = this.searchQuery().trim();
    if (!query) {
      return;
    }

    this.router.navigate(['global-search'], {
      queryParams: { q: query },
    });
  }

  logout() {
    this.oauth.logOut();
  }

  setActiveClient(client: string) {
    sessionStorage.setItem('activeClient', client);
  }
}
